import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { logoAztecaWebp, logoCuadradoWebp } from '../../assets/icons';
import { useCart } from '../../context/CartContext';

const Navbar = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  const { cartItems, removeFromCart, updateQuantity, clearCart, totalItems } = useCart();

  const handleMouseEnter = (dropdown) => setOpenDropdown(dropdown);
  const handleMouseLeave = () => setOpenDropdown(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) setMobileSubmenu(null);
  };

  const toggleMobileSubmenu = (menu) => {
    setMobileSubmenu(mobileSubmenu === menu ? null : menu);
  };

  const toggleCart = () => setCartOpen(!cartOpen);

  // Datos de maquinaria
  const machineryTypes = [
    { name: 'Brazos Articulados', slug: 'brazoarticulado' },
    { name: 'Cargadores Frontales', slug: 'cargadoresfrontales' },
    { name: 'Compactadoras', slug: 'compactadoras' },
    { name: 'Excavadoras', slug: 'excavadoras' },
    { name: 'Manipuladores Telescópicos', slug: 'manipuladorestelescopicos' },
    { name: 'Minicargadores', slug: 'minicargadores' },
    { name: 'Miniexcavadoras', slug: 'miniexcavadoras' },
    { name: 'Montacargas', slug: 'montacargas' },
    { name: 'Plataformas de Altura', slug: 'plataformadealtura' },
    { name: 'Retroexcavadoras', slug: 'retroexcavadora' },
    { name: 'Teletruk', slug: 'teletruk' }
  ];

  const industryTypes = [
    { name: 'Agricultura', slug: 'agricultura' },
    { name: 'Construcción', slug: 'construccion' },
    { name: 'Industrial', slug: 'industrial' },
    { name: 'OEM', slug: 'oem' },
    { name: 'Reciclaje y Residuos', slug: 'reciclaje-y-residuos' },
  ];

  const serviceTypes = [
    { name: 'Mantenimiento', anchor: 'maintenance' },
    { name: 'Diagnóstico y Reparación', anchor: 'diagnostic' },
    { name: 'Garantías Extendidas', anchor: 'warranty' },
    { name: 'Reconstrucciones Certificadas', anchor: 'reconstructions' },
    { name: 'JCB LiveLink', anchor: 'livelink' },
  ];

  // ============================================================
  // Separar tipos de item del carrito (3 tipos)
  // ============================================================
  const machineRequests = cartItems.filter(i => i.itemType === 'machineRequest');
  const machineryItems = cartItems.filter(i => i.itemType === 'machinery');
  const spareParts = cartItems.filter(i => i.itemType === 'sparePart');

  // Agrupar refacciones por contexto
  const sparePartGroups = Object.values(
    spareParts.reduce((acc, item) => {
      const key = item.contextKey || 'partNumber';
      if (!acc[key]) {
        acc[key] = {
          key,
          context: item.context || { source: 'partNumber' },
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    }, {})
  );

  const TrashIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const hasItems = totalItems > 0;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-border-subtle/80 transition-all duration-300">
      <div className="max-w-[1680px] mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group transition-opacity duration-200 hover:opacity-90">
          <img alt="JCB AZTECA" className="h-8 md:hidden w-auto object-contain" src={logoCuadradoWebp} />
          <img alt="JCB AZTECA" className="h-8 md:h-9 w-auto object-contain hidden md:block" src={logoAztecaWebp} />
        </Link>

        {/* Navigation Links - Desktop */}
        <nav className="hidden md:flex items-center space-x-8 lg:space-x-10">
          {/* Maquinaria */}
          <div className="relative" onMouseEnter={() => handleMouseEnter('machinery')} onMouseLeave={handleMouseLeave}>
            <Link to="/machinery" className="font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2 flex items-center gap-1">
              Maquinaria
              <span className="material-symbols-outlined text-sm transition-transform duration-200" style={{ transform: openDropdown === 'machinery' ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                expand_more
              </span>
            </Link>
            {openDropdown === 'machinery' && (
              <div className="absolute top-full left-0 mt-0 w-[280px] bg-white border border-border-subtle shadow-lg py-2">
                {machineryTypes.map((type) => (
                  <Link key={type.slug} to={`/machinery/${type.slug}`} className="block px-6 py-2.5 font-display text-xs uppercase tracking-luxury text-text-muted hover:text-dark-primary hover:bg-surface-subtle transition-colors">
                    {type.name}
                  </Link>
                ))}
                <div className="border-t border-border-subtle my-2"></div>
                <Link to="/machinery" className="block px-6 py-2.5 font-display text-xs uppercase tracking-luxury font-semibold text-jcb-yellow hover:bg-surface-subtle transition-colors">
                  Ver toda la maquinaria →
                </Link>
              </div>
            )}
          </div>

          <Link to="/spare-parts" className="font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2">
            Refacciones
          </Link>

          <div className="relative" onMouseEnter={() => handleMouseEnter('services')} onMouseLeave={handleMouseLeave}>
            <Link to="/services" className="font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2 flex items-center gap-1">
              Servicios
              <span className="material-symbols-outlined text-sm transition-transform duration-200" style={{ transform: openDropdown === 'services' ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                expand_more
              </span>
            </Link>
            {openDropdown === 'services' && (
              <div className="absolute top-full left-0 mt-0 w-[240px] bg-white border border-border-subtle shadow-lg py-2">
                {serviceTypes.map((service) => (
                  <a key={service.anchor} href={`/services#${service.anchor}`} className="block px-6 py-2.5 font-display text-xs uppercase tracking-luxury text-text-muted hover:text-dark-primary hover:bg-surface-subtle transition-colors">
                    {service.name}
                  </a>
                ))}
                <div className="border-t border-border-subtle my-2"></div>
                <Link to="/services" className="block px-6 py-2.5 font-display text-xs uppercase tracking-luxury font-semibold text-jcb-yellow hover:bg-surface-subtle transition-colors">
                  Ver todos los servicios →
                </Link>
              </div>
            )}
          </div>

          <div className="relative" onMouseEnter={() => handleMouseEnter('industries')} onMouseLeave={handleMouseLeave}>
            <Link to="/industries" className="font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2 flex items-center gap-1">
              Industrias
              <span className="material-symbols-outlined text-sm transition-transform duration-200" style={{ transform: openDropdown === 'industries' ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                expand_more
              </span>
            </Link>
            {openDropdown === 'industries' && (
              <div className="absolute top-full left-0 mt-0 w-[220px] bg-white border border-border-subtle shadow-lg py-2">
                {industryTypes.map((industry) => (
                  <Link key={industry.slug} to={`/industries/${industry.slug}`} className="block px-6 py-2.5 font-display text-xs uppercase tracking-luxury text-text-muted hover:text-dark-primary hover:bg-surface-subtle transition-colors">
                    {industry.name}
                  </Link>
                ))}
                <div className="border-t border-border-subtle my-2"></div>
                <Link to="/industries" className="block px-6 py-2.5 font-display text-xs uppercase tracking-luxury font-semibold text-jcb-yellow hover:bg-surface-subtle transition-colors">
                  Ver todas las industrias →
                </Link>
              </div>
            )}
          </div>

          <Link to="/about" className="font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2">
            Nosotros
          </Link>
        </nav>

        {/* Right Action Buttons - Desktop */}
        <div className="hidden md:flex items-center gap-4">
          <button onClick={toggleCart} className="relative inline-flex items-center justify-center w-10 h-10 text-dark-primary/70 hover:text-dark-primary transition-colors" aria-label="Carrito">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {hasItems && (
              <span className="absolute -top-1 -right-1 bg-jcb-yellow text-dark-primary text-[10px] font-display font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>

          <Link
            to={hasItems ? "/cart" : "/contact"}
            className={`inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium px-6 sm:px-8 py-3 rounded-none transition-all duration-300 border ${hasItems
              ? 'bg-jcb-yellow text-dark-primary border-jcb-yellow hover:bg-yellow-500'
              : 'bg-dark-primary text-white border-dark-primary hover:bg-black'
              }`}
          >
            {hasItems ? `Cotizar (${totalItems})` : 'Contactanos'}
          </Link>
        </div>

        <button onClick={toggleMobileMenu} className="md:hidden flex items-center justify-center w-10 h-10 text-dark-primary hover:text-jcb-yellow transition-colors" aria-label="Menú">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden bg-white border-b border-border-subtle transition-all duration-300 overflow-hidden ${mobileMenuOpen ? 'max-h-[80vh] overflow-y-auto opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 py-4 space-y-1">
          <div className="border-b border-border-subtle pb-2">
            <button onClick={() => toggleMobileSubmenu('machinery')} className="w-full flex items-center justify-between font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2">
              <span>Maquinaria</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${mobileSubmenu === 'machinery' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${mobileSubmenu === 'machinery' ? 'max-h-[600px]' : 'max-h-0'}`}>
              <div className="pl-4 space-y-1 border-l-2 border-jcb-yellow mt-1">
                {machineryTypes.map((type) => (
                  <Link key={type.slug} to={`/machinery/${type.slug}`} className="block font-display text-xs uppercase tracking-luxury text-text-muted hover:text-dark-primary transition-colors py-1.5" onClick={() => setMobileMenuOpen(false)}>
                    {type.name}
                  </Link>
                ))}
                <Link to="/machinery" className="block font-display text-xs uppercase tracking-luxury font-semibold text-jcb-yellow hover:text-dark-primary transition-colors py-1.5" onClick={() => setMobileMenuOpen(false)}>
                  Ver toda la maquinaria →
                </Link>
              </div>
            </div>
          </div>

          <Link to="/spare-parts" className="block font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2 border-b border-border-subtle" onClick={() => setMobileMenuOpen(false)}>
            Refacciones
          </Link>

          <div className="border-b border-border-subtle pb-2">
            <button onClick={() => toggleMobileSubmenu('services')} className="w-full flex items-center justify-between font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2">
              <span>Servicios</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${mobileSubmenu === 'services' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${mobileSubmenu === 'services' ? 'max-h-[400px]' : 'max-h-0'}`}>
              <div className="pl-4 space-y-1 border-l-2 border-jcb-yellow mt-1">
                {serviceTypes.map((service) => (
                  <a key={service.anchor} href={`/services#${service.anchor}`} className="block font-display text-xs uppercase tracking-luxury text-text-muted hover:text-dark-primary transition-colors py-1.5" onClick={() => setMobileMenuOpen(false)}>
                    {service.name}
                  </a>
                ))}
                <Link to="/services" className="block font-display text-xs uppercase tracking-luxury font-semibold text-jcb-yellow hover:text-dark-primary transition-colors py-1.5" onClick={() => setMobileMenuOpen(false)}>
                  Ver todos los servicios →
                </Link>
              </div>
            </div>
          </div>

          <div className="border-b border-border-subtle pb-2">
            <button onClick={() => toggleMobileSubmenu('industries')} className="w-full flex items-center justify-between font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2">
              <span>Industrias</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${mobileSubmenu === 'industries' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${mobileSubmenu === 'industries' ? 'max-h-[400px]' : 'max-h-0'}`}>
              <div className="pl-4 space-y-1 border-l-2 border-jcb-yellow mt-1">
                {industryTypes.map((industry) => (
                  <Link key={industry.slug} to={`/industries/${industry.slug}`} className="block font-display text-xs uppercase tracking-luxury text-text-muted hover:text-dark-primary transition-colors py-1.5" onClick={() => setMobileMenuOpen(false)}>
                    {industry.name}
                  </Link>
                ))}
                <Link to="/industries" className="block font-display text-xs uppercase tracking-luxury font-semibold text-jcb-yellow hover:text-dark-primary transition-colors py-1.5" onClick={() => setMobileMenuOpen(false)}>
                  Ver todas las industrias →
                </Link>
              </div>
            </div>
          </div>

          <Link to="/about" className="block font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2 border-b border-border-subtle" onClick={() => setMobileMenuOpen(false)}>
            Nosotros
          </Link>

          <Link to="/contact" className="block font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2 border-b border-border-subtle" onClick={() => setMobileMenuOpen(false)}>
            Contacto
          </Link>

          <Link to="/cart" className="font-display text-xs uppercase tracking-luxury text-dark-primary/70 hover:text-dark-primary transition-colors py-2 border-b border-border-subtle flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            Carrito
            {hasItems && (
              <span className="bg-jcb-yellow text-dark-primary text-[10px] font-display font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </Link>

          <Link to={hasItems ? "/cart" : "/contact"} className="block w-full text-center font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-6 py-3 transition-all duration-300 mt-2" onClick={() => setMobileMenuOpen(false)}>
            {hasItems ? `Cotizar (${totalItems})` : 'Contactanos'}
          </Link>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Dropdown del Carrito — 3 secciones                            */}
      {/* ============================================================ */}
      <div className={`absolute top-full right-0 w-[440px] max-w-[95vw] bg-white border border-border-subtle shadow-lg transition-all duration-300 origin-top-right ${cartOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}`}>
        <div className="p-4">
          <div className="flex items-center justify-between border-b border-border-subtle pb-3 mb-3">
            <h3 className="font-display text-sm font-semibold text-dark-primary">Carrito</h3>
            <button onClick={toggleCart} className="text-text-muted hover:text-dark-primary transition-colors" aria-label="Cerrar">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {cartItems.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-text-muted text-sm">Tu carrito está vacío</p>
            </div>
          ) : (
            <>
              <div className="max-h-[440px] overflow-y-auto space-y-5">

                {/* 1) MAQUINARIA */}
                {machineryItems.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-display text-[10px] uppercase tracking-luxury text-text-muted font-semibold">
                      Maquinaria
                    </p>
                    <div className="border border-border-subtle divide-y divide-border-subtle">
                      {machineryItems.map((item) => (
                        <div key={item.id} className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-display text-sm font-semibold text-dark-primary truncate">
                                {item.machineryType || item.name}
                              </p>
                              {item.model && (
                                <p className="font-display text-xs text-dark-primary/70 truncate mt-0.5">
                                  {item.model}
                                </p>
                              )}
                              <p className="text-xs font-display font-medium text-jcb-yellow mt-1">
                                {item.price === 0 || item.price == null ? (
                                  'A cotizar'
                                ) : (
                                  `$${Number(item.price).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })} USD`
                                )}
                              </p>
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0">
                              {item.machineryId && (
                                <>
                                  <button
                                    onClick={() => updateQuantity(item.id, item.quantity - 1, item.contextKey)}
                                    className="w-6 h-6 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                  >
                                    -
                                  </button>
                                  <span className="w-6 text-center text-xs font-display text-dark-primary">
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() => updateQuantity(item.id, item.quantity + 1, item.contextKey)}
                                    className="w-6 h-6 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                  >
                                    +
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => removeFromCart(item.id, item.contextKey)}
                                className="w-6 h-6 flex items-center justify-center text-text-muted hover:text-red-500 transition-colors ml-1"
                                aria-label="Quitar"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2) REFACCIONES */}
                {sparePartGroups.length > 0 && (
                  <div className="space-y-3">
                    <p className="font-display text-[10px] uppercase tracking-luxury text-text-muted font-semibold">
                      Refacciones
                    </p>

                    {sparePartGroups.map((group) => (
                      <div key={group.key} className="border border-border-subtle">
                        <div className="divide-y divide-border-subtle">
                          {group.items.map((item) => (
                            <div key={`${item.id}-${item.contextKey}`} className="flex items-center gap-3 p-3">
                              <div className="flex-1 min-w-0">
                                <p className="font-display text-xs font-medium text-dark-primary truncate">
                                  {item.name}
                                </p>
                                <p className="text-xs font-display font-medium text-jcb-yellow mt-1">
                                  {item.price === 0 || item.price == null ? (
                                    'A cotizar'
                                  ) : (
                                    `$${Number(item.price).toLocaleString('en-US', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })} USD`
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity - 1, item.contextKey)}
                                  className="w-6 h-6 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                >
                                  -
                                </button>
                                <span className="w-6 text-center text-xs font-display text-dark-primary">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity + 1, item.contextKey)}
                                  className="w-6 h-6 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                >
                                  +
                                </button>
                                <button
                                  onClick={() => removeFromCart(item.id, item.contextKey)}
                                  className="w-6 h-6 flex items-center justify-center text-text-muted hover:text-red-500 transition-colors ml-1"
                                  aria-label="Quitar"
                                >
                                  <TrashIcon />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 3) SOLICITUDES DE REFACCIONES */}
                {machineRequests.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-display text-[10px] uppercase tracking-luxury text-text-muted font-semibold">
                      Solicitudes de Refacciones
                    </p>
                    {machineRequests.map((req) => (
                      <div key={req.id} className="border border-border-subtle">
                        <div className="px-3 py-3 flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-display text-sm font-semibold text-dark-primary truncate">
                              {req.machineryType}
                            </p>
                            <p className="font-display text-xs text-dark-primary/70 truncate mt-0.5">
                              {req.model} {req.year}
                            </p>
                            <p className="text-xs text-text-muted mt-1 truncate">
                              Área: {req.area}
                            </p>
                          </div>
                          <button
                            onClick={() => removeFromCart(req.id, null)}
                            className="text-text-muted hover:text-red-500 transition-colors flex-shrink-0 mt-0.5"
                            aria-label="Quitar solicitud"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="border-t border-border-subtle pt-3 mt-3">
                <div className="flex gap-2">
                  <button onClick={clearCart} className="flex-1 font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-text-muted hover:text-dark-primary hover:border-dark-primary px-4 py-2 transition-all">
                    Vaciar
                  </button>
                  <Link to="/cart" onClick={toggleCart} className="flex-1 font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-4 py-2 transition-all text-center">
                    Ver Carrito
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;